// El paso de ELEGIR PRODUCTOS de una sesión con el agente, compartido por el Catálogo y Videos
// cortos (ADR-2026-10-30-videos-cortos-con-el-agente). Es, tal cual, el paso 1 de la portada del
// Catálogo (ADR-2026-10-13-catalogo-con-el-agente, ADR-2026-10-14-el-pie-de-elegir-productos-es-el-del-agente):
// la grilla de `PickProductPanel` en la tarjeta blanca de los modos, el pie flotante del selector
// del agente —las miniaturas de lo elegido a la izquierda, cada foto tocada VUELA hasta la suya—,
// el contador del tope (ADR-2026-10-14-el-tope-de-productos-se-ve) y "Siguiente". Lo que cambia
// entre modos entra por props: el título, las migas, el tope y lo que va a la derecha del título
// (la lista de sesiones anteriores). La ruta que lo monta tiene que estar en `RUTAS_SHELL_LEGACY`:
// el pie flota con `absolute bottom-0` y necesita el alto entero del `<main>`
// (ADR-2026-10-30-videos-cortos-se-hospeda-en-neutro).
import { ArrowRight, ChevronDown, Info } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { MiniaturasElegidas } from '../../../../chat-atlas/ui/MiniaturasElegidas';
import { EncabezadoPantalla } from '../../../../components/PantallaChrome';
import { useDebajoDeMd } from '../../../../hooks/useEsMobile';
import { PickProductPanel } from '../../components/PickProductPanel';
import { SubirProductoDrawer } from '../../components/SubirProductoDrawer';
import type { MockProductV2 } from '../../mockData';
import { estiloDelMarcoTelefono, MARCO_TELEFONO } from '../catalogoAgente/marcoDelTelefono';
import { volarAlPie } from '../catalogoAgente/vueloAlPie';

/** Lo que la barra flotante tapa del borde de abajo: el aire que la grilla deja al final. */
const ESPACIO_DE_LA_BARRA = 136;
/** Lo que tarda la barra en aparecer (la `duration-300` de su transición). */
const ENTRADA_DE_LA_BARRA_MS = 300;

const fechaDeSesion = (iso: string): string => {
  const d = new Date(iso);
  return `${d.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })}, ${d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}`;
};

/**
 * Cuántos van del tope: un anillo que se llena y el número. Cerca del tope se pone ámbar y,
 * lleno, dice que es el máximo (ADR-2026-10-14-el-tope-de-productos-se-ve).
 */
const ContadorDelTope: React.FC<{ cantidad: number; max: number; unidad: string }> = ({ cantidad, max, unidad }) => {
  const parte = Math.min(1, cantidad / max);
  const lleno = cantidad >= max;
  const cerca = !lleno && cantidad >= max * 0.9;
  const color = lleno ? '#B54708' : cerca ? '#DC6803' : 'rgb(var(--brand-primary-rgb))';
  const r = 8;
  const largo = 2 * Math.PI * r;
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-2 rounded-full py-1.5 pl-2 pr-3 text-[13px] font-semibold transition-colors ${
        lleno ? 'bg-[#FFF4E5] text-[#B54708]' : cerca ? 'bg-[#FFF8EB] text-[#B54708]' : 'text-[rgb(var(--brand-black-rgb)_/_0.6)]'
      }`}
      title={`Hasta ${max} productos por ${unidad}`}
    >
      <svg viewBox="0 0 20 20" className="h-5 w-5 -rotate-90" aria-hidden="true">
        <circle cx="10" cy="10" r={r} fill="none" stroke="currentColor" strokeOpacity={0.15} strokeWidth="2.5" />
        <circle
          cx="10"
          cy="10"
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray={largo}
          strokeDashoffset={largo * (1 - parte)}
          style={{ transition: 'stroke-dashoffset .3s ease-out, stroke .2s' }}
        />
      </svg>
      {lleno ? `${max} de ${max}: el máximo` : `${cantidad} de ${max}`}
    </span>
  );
};

export interface SesionResumen {
  id: string;
  titulo: string;
  actualizadoEn: string;
}

/**
 * "Mis catálogos" / "Mis tandas": la lista va en un PORTAL al body, con posición fija debajo
 * del botón. Adentro de la cabecera del selector de productos quedaba en el mismo contexto de
 * apilado que su barra de búsqueda pegajosa, que le pasaba por encima.
 */
export const ListaDeSesiones: React.FC<{ etiqueta: string; sesiones: SesionResumen[]; onAbrir: (id: string) => void }> = ({ etiqueta, sesiones, onAbrir }) => {
  const botonRef = useRef<HTMLButtonElement>(null);
  const [lugar, setLugar] = useState<{ top: number; right: number } | null>(null);
  const cerrar = useCallback(() => setLugar(null), []);
  useEffect(() => {
    if (!lugar) return;
    const alTeclado = (e: KeyboardEvent) => e.key === 'Escape' && cerrar();
    window.addEventListener('resize', cerrar);
    window.addEventListener('keydown', alTeclado);
    return () => {
      window.removeEventListener('resize', cerrar);
      window.removeEventListener('keydown', alTeclado);
    };
  }, [lugar, cerrar]);
  if (sesiones.length === 0) return null;
  const alternar = () => {
    if (lugar) return cerrar();
    const r = botonRef.current?.getBoundingClientRect();
    if (r) setLugar({ top: r.bottom + 8, right: window.innerWidth - r.right });
  };
  return (
    <div className="shrink-0">
      <button
        ref={botonRef}
        type="button"
        onClick={alternar}
        aria-expanded={!!lugar}
        className="inline-flex items-center gap-1.5 rounded-full bg-white py-2 px-4 text-[14px] font-semibold text-brand-black shadow-sm ring-1 ring-black/[0.06] transition-colors hover:bg-black/[0.03]"
      >
        {etiqueta}
        <ChevronDown className={`h-4 w-4 transition-transform ${lugar ? 'rotate-180' : ''}`} strokeWidth={2} aria-hidden="true" />
      </button>
      {lugar &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[200]" onClick={cerrar} aria-hidden="true" />
            <ul
              className="fixed z-[201] max-h-[min(420px,calc(100vh-120px))] w-[300px] overflow-y-auto rounded-2xl bg-white p-2 shadow-lg ring-1 ring-black/[0.06]"
              style={{ top: lugar.top, right: lugar.right }}
            >
              {sesiones.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => {
                      cerrar();
                      onAbrir(s.id);
                    }}
                    className="flex w-full flex-col items-start rounded-xl px-3 py-2 text-left transition-colors hover:bg-black/[0.04]"
                  >
                    <span className="w-full truncate text-[14px] font-semibold text-brand-black">{s.titulo}</span>
                    <span className="text-[12px] text-[#3C3C43]/60">{fechaDeSesion(s.actualizadoEn)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </>,
          document.body,
        )}
    </div>
  );
};

export interface ElegirProductosDeSesionProps {
  /** "Elegí los productos para el catálogo". */
  titulo: string;
  migas: Array<{ label: string; onClick?: () => void }>;
  maxProductos: number;
  /** Cómo se llama lo que se arma, para el tope: "catálogo", "tanda". */
  unidad: string;
  /** Lo que va a la derecha del título (la lista de sesiones anteriores). */
  derecha?: ReactNode;
  rubro: string | null;
  companyId: string | null;
  brandId: string | null;
  /** Se dio de alta un producto: el host vuelve a traer el catálogo de la marca. */
  onProductosNuevos?: () => void;
  elegidos: MockProductV2[];
  onElegidos: (lista: MockProductV2[]) => void;
  /** Por qué no se puede seguir (vacío = se puede). */
  motivoBloqueo: string | null;
  onSiguiente: () => void;
  /** Salir del modo (atrás del selector). */
  onSalir: () => void;
}

export const ElegirProductosDeSesion: React.FC<ElegirProductosDeSesionProps> = ({
  titulo,
  migas,
  maxProductos,
  unidad,
  derecha,
  rubro,
  companyId,
  brandId,
  onProductosNuevos,
  elegidos,
  onElegidos,
  motivoBloqueo,
  onSiguiente,
  onSalir,
}) => {
  // En el teléfono la grilla elige sola 2 o 3 columnas y la tarjeta ocupa toda la pantalla
  // (ADR-2026-10-15-el-catalogo-en-el-telefono).
  const esTelefono = useDebajoDeMd();
  // La tarjeta "Subir nuevo producto" de la grilla abre el alta, como en los otros modos.
  const [subirProductoAbierto, setSubirProductoAbierto] = useState(false);

  // ── El pie: lo elegido, con el vuelo de cada foto que se toca ─────────────
  const grillaRef = useRef<HTMLDivElement>(null);
  const pieRef = useRef<HTMLDivElement>(null);
  const [clavePanel, setClavePanel] = useState(0);
  const previosRef = useRef<Set<string> | null>(null);
  const vuelos = useRef(new Set<() => void>());
  useEffect(() => {
    const enCurso = vuelos.current;
    return () => {
      for (const cancelar of enCurso) cancelar();
      enCurso.clear();
    };
  }, []);
  const cambiarSeleccion = useCallback(
    (lista: MockProductV2[]) => {
      const previos = previosRef.current;
      previosRef.current = new Set(lista.map((p) => p.id));
      onElegidos(lista);
      // El primer aviso del panel (al montar) trae lo que ya estaba: eso no vuela.
      if (!previos) return;
      // Con la barra escondida (no había nada elegido), el vuelo espera a que termine de entrar.
      const espera = previos.size === 0 ? ENTRADA_DE_LA_BARRA_MS : 0;
      for (const p of lista) {
        if (previos.has(p.id)) continue;
        const cancelar = volarAlPie(p.id, grillaRef.current, pieRef.current, espera);
        vuelos.current.add(cancelar);
        window.setTimeout(() => vuelos.current.delete(cancelar), 1_000 + espera);
      }
    },
    [onElegidos],
  );
  /** La X de una miniatura: es tocar su tarjeta en la grilla (el mismo camino que elegirla). Si
   *  el buscador la está escondiendo, el panel se vuelve a montar sin ella. */
  const quitarElegido = useCallback(
    (id: string) => {
      const tarjeta = grillaRef.current?.querySelector<HTMLElement>(`[data-tira-tile="${CSS.escape(id)}"]`);
      if (tarjeta) {
        tarjeta.click();
        return;
      }
      const restantes = elegidos.filter((p) => p.id !== id);
      previosRef.current = null;
      onElegidos(restantes);
      setClavePanel((n) => n + 1);
    },
    [elegidos, onElegidos],
  );

  // ── El tope (ADR-2026-10-14-el-tope-de-productos-se-ve) ────────────────────
  // Con el tope lleno, tocar un producto que no está elegido no lo suma: el toque se ataja en la
  // captura (antes de que llegue a la tarjeta), la barra se sacude y avisa arriba. Las tarjetas
  // que ya no entran se ven apagadas.
  const alTope = elegidos.length >= maxProductos;
  const [avisoDeTope, setAvisoDeTope] = useState(0);
  useEffect(() => {
    if (!avisoDeTope) return;
    pieRef.current?.animate(
      [
        { transform: 'translateX(0)' },
        { transform: 'translateX(-8px)' },
        { transform: 'translateX(7px)' },
        { transform: 'translateX(-4px)' },
        { transform: 'translateX(2px)' },
        { transform: 'translateX(0)' },
      ],
      { duration: 380, easing: 'ease-out' },
    );
    const t = window.setTimeout(() => setAvisoDeTope(0), 2_800);
    return () => window.clearTimeout(t);
  }, [avisoDeTope]);
  const atajarAlTope = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      if (!alTope) return;
      if ('key' in e && e.key !== 'Enter' && e.key !== ' ') return;
      const tarjeta = (e.target as HTMLElement).closest<HTMLElement>('[data-tira-tile]');
      const id = tarjeta?.getAttribute('data-tira-tile');
      if (!id || elegidos.some((p) => p.id === id)) return;
      e.preventDefault();
      e.stopPropagation();
      setAvisoDeTope((n) => n + 1);
    },
    [alTope, elegidos],
  );
  /** Con el tope lleno, las tarjetas que no están elegidas se apagan (las elegidas no). */
  const estiloDelTope = alTope
    ? `[data-grilla-catalogo] [data-tira-tile]{opacity:.4;filter:grayscale(.5);cursor:not-allowed;transition:opacity .2s,filter .2s}` +
      elegidos.map((p) => `[data-grilla-catalogo] [data-tira-tile="${CSS.escape(p.id)}"]`).join(',') +
      `{opacity:1;filter:none;cursor:pointer}`
    : '';

  // Migas y el título grande con su separación antes de la grilla
  // (ADR-2026-10-14-elegir-productos-con-aire).
  const cabecera = (
    <div className="flex flex-wrap items-end justify-between gap-3 px-4 pt-4 pb-3 md:gap-6 md:px-6 md:pt-6">
      <div>
        {/* En el teléfono las migas no van: el botón de atrás del shell es la salida. */}
        <EncabezadoPantalla migas={migas} className="!px-0 !pt-0 !pb-4 hidden md:block" />
        <h1 className="text-[22px] font-semibold md:text-[28px] leading-tight tracking-[-0.01em] text-brand-black">{titulo}</h1>
      </div>
      {derecha}
    </div>
  );
  return (
    // La geometría de las pantallas de modo (Ambiente completo, Automático): el gris de la app
    // —el mismo de detrás de la sidebar— con 52 px de aire, y la grilla en UNA tarjeta blanca
    // (ADR-2026-10-14-elegir-productos-va-en-la-tarjeta-de-los-modos).
    <div className={`flex h-full min-h-0 flex-col bg-surface-mobile md:px-[52px] md:py-[52px] ${MARCO_TELEFONO}`} style={estiloDelMarcoTelefono(esTelefono)}>
      <div className="relative mx-auto flex min-h-0 w-full max-w-[1320px] flex-1 flex-col overflow-hidden bg-white md:rounded-2xl md:shadow-sm md:ring-1 md:ring-black/[0.04]">
        {estiloDelTope && <style>{estiloDelTope}</style>}
        <div ref={grillaRef} data-grilla-catalogo="" className="relative flex min-h-0 flex-1 flex-col" onClickCapture={atajarAlTope} onKeyDownCapture={atajarAlTope}>
          <PickProductPanel
            key={clavePanel}
            variant="desktop"
            hideBack
            hideFooter
            rubro={rubro}
            initialSelectedIds={elegidos.map((p) => p.id)}
            hideHeader
            cabecera={cabecera}
            nombreSobreLaFoto
            columnasFijas={esTelefono ? undefined : 5}
            ocultarBloqueSubida
            onSubirProducto={() => setSubirProductoAbierto(true)}
            onSelectionChange={cambiarSeleccion}
            espacioAlPie={ESPACIO_DE_LA_BARRA}
            // Al scrollear se pliegan el título y el recuento: arriba queda sólo el buscador.
            plegarAlScrollear
            onConfirm={onSiguiente}
            onBack={onSalir}
          />
        </div>
        {/* El pie del selector del agente, FLOTANDO sobre el borde de abajo de la grilla (no una
            franja blanca a lo ancho): lo elegido a la izquierda y el botón a la derecha. */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 flex flex-col items-center gap-2 px-3 pb-[max(env(safe-area-inset-bottom),12px)] md:px-12 md:pb-6">
          {avisoDeTope > 0 && (
            <p key={avisoDeTope} role="status" className="animate-fade-in pointer-events-auto inline-flex items-center gap-2 rounded-full bg-brand-black py-2 px-4 text-[13px] font-medium text-white shadow-lg">
              <Info className="h-4 w-4 shrink-0" strokeWidth={2.25} aria-hidden="true" />
              Llegaste a {maxProductos} productos, el máximo por {unidad}. Sacá uno para sumar otro.
            </p>
          )}
          {/* Escondida hasta el primer producto: entra subiendo y con un fundido, y sale igual
              al sacar el último. Sigue montada para que las miniaturas hagan su salida. */}
          <div
            ref={pieRef}
            aria-hidden={elegidos.length === 0}
            className={`flex w-full max-w-[1040px] items-center gap-3 rounded-[26px] bg-white/90 py-3 pl-4 pr-3 md:gap-4 md:py-3.5 md:pl-6 md:pr-3.5 shadow-[0_16px_48px_-14px_rgb(var(--brand-black-rgb)_/_0.3),0_2px_8px_rgb(var(--brand-black-rgb)_/_0.06)] ring-1 ring-black/[0.06] backdrop-blur-md transition-[opacity,transform] duration-300 ease-out motion-reduce:transition-none ${
              elegidos.length > 0 ? 'pointer-events-auto translate-y-0 opacity-100' : 'pointer-events-none translate-y-5 opacity-0'
            }`}
          >
            <MiniaturasElegidas
              items={elegidos.map((p) => ({ id: p.id, url: p.imageUrl ?? null, nombre: p.name }))}
              onQuitar={quitarElegido}
              className="flex flex-1"
              tamano={esTelefono ? 'normal' : 'grande'}
            />
            {elegidos.length > 0 && <ContadorDelTope cantidad={elegidos.length} max={maxProductos} unidad={unidad} />}
            <button
              type="button"
              onClick={onSiguiente}
              disabled={!!motivoBloqueo}
              tabIndex={elegidos.length === 0 ? -1 : undefined}
              title={motivoBloqueo ?? undefined}
              className="inline-flex shrink-0 items-center gap-2 rounded-full bg-brand-primary py-3 px-5 text-[15px] font-semibold text-white md:py-3.5 md:px-7 md:text-[16px] transition-colors hover:bg-brand-primary-hover disabled:cursor-not-allowed disabled:opacity-40"
            >
              Siguiente
              <ArrowRight className="h-[18px] w-[18px]" strokeWidth={2.25} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
      {/* Alta de producto desde la tarjeta "Subir nuevo producto" de la grilla: el mismo drawer
          que Ambiente completo; al terminar, la pantalla vuelve a traer los productos. */}
      <SubirProductoDrawer
        open={subirProductoAbierto}
        onClose={() => setSubirProductoAbierto(false)}
        userCompanyId={companyId}
        brandId={brandId}
        rubro={rubro}
        onProductsRefreshed={() => onProductosNuevos?.()}
      />
    </div>
  );
};
